import { Color } from 'three';

/**
 * Convert hue (0-1) to RGB color
 * Uses HSL with full saturation and 50% lightness
 *
 * @param hue Hue value from 0 to 1 (0 = red, 0.33 = green, 0.66 = blue)
 * @param saturation Saturation from 0 to 1 (default: 1.0)
 * @param lightness Lightness from 0 to 1 (default: 0.5)
 * @returns Three.js Color object
 */
export const hueToColor = (
	hue: number,
	saturation: number = 1.0,
	lightness: number = 0.5
): Color => {
	const color = new Color();
	color.setHSL(hue, saturation, lightness);
	return color;
};

/**
 * Convert angle in radians to hue (0-1)
 * Maps full circle (0 to 2π) to hue range (0 to 1)
 *
 * @param angle Angle in radians
 * @returns Hue value from 0 to 1
 */
export const angleToHue = (angle: number): number => {
	// Normalize angle to 0-2π range
	let normalizedAngle = angle % (Math.PI * 2);
	if (normalizedAngle < 0) {
		normalizedAngle += Math.PI * 2;
	}
	// Convert to 0-1 range
	return normalizedAngle / (Math.PI * 2);
};

/**
 * Convert angle in radians directly to a Color
 *
 * @param angle Angle in radians
 * @returns Three.js Color object
 */
export const angleToColor = (angle: number): Color => {
	const hue = angleToHue(angle);
	return hueToColor(hue);
};

/**
 * Get color from a position on a color wheel
 * Position is relative to center of wheel
 *
 * @param x X position relative to center
 * @param y Y position relative to center
 * @returns Three.js Color object
 */
export const positionToColor = (x: number, y: number): Color => {
	const angle = Math.atan2(y, x);
	return angleToColor(angle);
};

/**
 * Calculate drag angle from start to current position
 *
 * @param startX Starting X position
 * @param startY Starting Y position
 * @param currentX Current X position
 * @param currentY Current Y position
 * @param centerX Center X (optional, for calculating relative angle)
 * @param centerY Center Y (optional, for calculating relative angle)
 * @returns Angle delta in radians
 */
export const calculateDragAngle = (
	startX: number,
	startY: number,
	currentX: number,
	currentY: number,
	centerX: number = 0,
	centerY: number = 0
): number => {
	const startAngle = Math.atan2(startY - centerY, startX - centerX);
	const currentAngle = Math.atan2(currentY - centerY, currentX - centerX);
	return currentAngle - startAngle;
};

/**
 * Predefined color palette for the color wheel
 * 12 colors representing the color wheel at 30° intervals
 */
export const COLOR_WHEEL_PALETTE = [
	0xff0000, // 0° - Red
	0xff8000, // 30° - Orange
	0xffff00, // 60° - Yellow
	0x80ff00, // 90° - Yellow-Green
	0x00ff00, // 120° - Green
	0x00ff80, // 150° - Green-Cyan
	0x00ffff, // 180° - Cyan
	0x0080ff, // 210° - Cyan-Blue
	0x0000ff, // 240° - Blue
	0x8000ff, // 270° - Blue-Magenta
	0xff00ff, // 300° - Magenta
	0xff0080 // 330° - Magenta-Red
] as const;

/**
 * Get a color from the predefined palette by index
 *
 * @param index Index into the color palette (wraps around)
 * @returns Hex color value
 */
export const getColorFromPalette = (index: number): number => {
	const normalizedIndex = ((index % COLOR_WHEEL_PALETTE.length) + COLOR_WHEEL_PALETTE.length) % COLOR_WHEEL_PALETTE.length;
	return COLOR_WHEEL_PALETTE[normalizedIndex];
};

/**
 * Get the nearest palette color for a given hue
 *
 * @param hue Hue value from 0 to 1
 * @returns Hex color value from palette
 */
export const getNearestPaletteColor = (hue: number): number => {
	const index = Math.round(hue * COLOR_WHEEL_PALETTE.length) % COLOR_WHEEL_PALETTE.length;
	return COLOR_WHEEL_PALETTE[index];
};

/**
 * Lerp between two colors
 *
 * @param color1 First color
 * @param color2 Second color
 * @param t Interpolation factor (0-1)
 * @returns Interpolated color
 */
export const lerpColor = (color1: Color, color2: Color, t: number): Color => {
	const result = new Color();
	result.lerpColors(color1, color2, t);
	return result;
};

